import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSave,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
} from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";

const PAGE_MODE = {
  ADD: "ADD",
  PRE: "PRE",
  FINAL: "FINAL",
};

const EstimationDetails = () => {
  const BASE_URL = "https://nissi-india.com";
  const API = `${BASE_URL}/api/estimationDetails`;

  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  const [showForm, setShowForm] = useState(true);
  const [workType, setWorkType] = useState("work");
  const [selectedRows, setSelectedRows] = useState([]);

  const [boqCode, setBoqCode] = useState("");
  const [workName, setWorkName] = useState("");
  const [tenderAuthority, setTenderAuthority] = useState("");
  const [tenderPackageNo, setTenderPackageNo] = useState("");
  const [builderName, setBuilderName] = useState("");

  const [showWorkTypeTable, setShowWorkTypeTable] = useState(false);
  const [estimationTypes, setEstimationTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [itemCategoryId, setItemCategoryId] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");

  const [itemCategories, setItemCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [itemsMode, setItemsMode] = useState("top100");
  const [itemsLoading, setItemsLoading] = useState(false);

  const [showEstimationTable, setShowEstimationTable] = useState(true);
  const [selectedEstimationTypeId, setSelectedEstimationTypeId] =
    useState(null);

  const [workTypes, setWorkTypes] = useState([]);
  const [workTypesLoading, setWorkTypesLoading] = useState(false);
  const [selectedWorkTypeIds, setSelectedWorkTypeIds] = useState([]);

  const [selectedWorkTypeId, setSelectedWorkTypeId] = useState(null);

  const [showWorkSubTypeTable, setShowWorkSubTypeTable] = useState(false);

  const [workSubTypes, setWorkSubTypes] = useState([]);
  const [workSubTypesLoading, setWorkSubTypesLoading] = useState(false);
  const [selectedWorkSubTypeIds, setSelectedWorkSubTypeIds] = useState([]);
  const [estimationDetails, setEstimationDetails] = useState([]);
  const [selectedDetailIds, setSelectedDetailIds] = useState([]);
  const [showSaveGrid, setShowSaveGrid] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [materialsDetails, setMaterialsDetails] = useState([]);

  const [pageMode, setPageMode] = useState(PAGE_MODE.ADD);
  const [tenderStatus, setTenderStatus] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [preEstimationId, setPreEstimationId] = useState(null);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const toDec = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const calcRow = (row) => {
    const nos = toNum(row.nos);
    const length = toNum(row.length);
    const breadth = toNum(row.breadth);
    const height = toNum(row.height);
    const rate = toNum(row.rate);
    const unit = toNum(row.unit);

    const qty = length * breadth * height * nos;

    let amount = 0;
    if (qty > 0 && unit > 0) {
      const actQty = qty / unit;
      amount = actQty * rate;
    }

    return {
      ...row,
      qty,
      amount,
    };
  };

  useEffect(() => {
    const loadForEdit = async () => {
      if (!isEdit) return;

      try {
        setLoadingExisting(true);

        const res = await axios.get(`${BASE_URL}/api/boq/getForEdit`, {
          params: { id },
          headers: commonHeaders,
        });

        const data = res.data;
        if (!data?.success) {
          toast.error(data?.message || "Failed to load BOQ");
          return;
        }

        setPageMode(data.mode);
        setTenderStatus(Number(data.tenderStatus || 0));
        setCanEdit(false);

        setBoqCode(data.header?.estimationCode || "");
        setWorkName(data.header?.projectName || "");
        setPreEstimationId(data.preEstimationId);
        setTenderAuthority(data.header?.customerName || "");
        setTenderPackageNo(data.header?.customerAddress || "");
        setBuilderName(data.header?.remarks || "");

        const normalizedDetails = (data.details || []).map((r) => {
          const row = {
            ...r,
            nos: r.nos ?? r.fld_nos ?? 0,
            length: r.length ?? r.fld_length ?? 0,
            breadth: r.breadth ?? r.fld_breadth ?? 0,
            height: r.height ?? r.fld_height ?? 0,
            rate: r.rate ?? r.fld_rate ?? 0,
            unit: r.unit ?? r.fld_unit ?? 1,
          };
          return calcRow(row);
        });

        setEstimationDetails(normalizedDetails);
        setShowSaveGrid(true);
      } catch (err) {
        console.error("loadForEdit error:", err);
        toast.error(err?.response?.data?.message || "Failed to load BOQ");
      } finally {
        setLoadingExisting(false);
      }
    };

    loadForEdit();
  }, [isEdit, id, BASE_URL, commonHeaders]);

  useEffect(() => {
    const loadNextCode = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/boq/nextEstCode`, {
          headers: commonHeaders,
        });

        if (res.data?.success) {
          setBoqCode(res.data.estCode || "");
        } else {
          toast.error("Failed to generate BOQ Code");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to generate BOQ Code");
      }
    };

    loadNextCode();
  }, [BASE_URL, commonHeaders]);

  const loadEstimationTypes = async (t = "work") => {
    try {
      setLoadingTypes(true);
      const res = await axios.get(`${BASE_URL}/api/boq/estimationTypes`, {
        params: { type: t },
        headers: commonHeaders,
      });

      const list = res.data?.list || [];
      setEstimationTypes(list);
      setSelectedRows([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load estimation types");
    } finally {
      setLoadingTypes(false);
    }
  };

  useEffect(() => {
    loadEstimationTypes("work");
  }, []);

  const loadItemCategories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/boq/itemCategories`, {
        headers: commonHeaders,
      });
      setItemCategories(res.data?.list || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load item categories");
    }
  };

  const loadItems = async (mode = "top100", useFilters = false) => {
    try {
      setItemsLoading(true);
      setItemsMode(mode);

      const params = { mode };

      if (useFilters) {
        if (itemCode) params.itemcode = itemCode;
        if (itemName) params.itemname = itemName;
        if (itemCategoryId) params.categoryid = itemCategoryId;
      }

      const res = await axios.get(`${BASE_URL}/api/boq/items`, {
        params,
        headers: commonHeaders,
      });

      setItems(res.data?.rows || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load items");
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    if (workType === "materials") {
      loadItemCategories();
      loadItems("top100", false);
    }
  }, [workType]);

  const handleMaterialSearch = () => {
    loadItems("top100", true);
  };

  const handleViewAllItems = () => {
    loadItems("all", true);
  };

  const loadWorkTypes = async (estimationTypeId) => {
    try {
      setWorkTypesLoading(true);
      const res = await axios.get(
        `${BASE_URL}/api/boq/workTypesByEstimationType`,
        {
          params: { estimationTypeId },
          headers: commonHeaders,
        },
      );

      setWorkTypes(res.data?.list || []);
      setSelectedWorkTypeIds([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Work Types");
    } finally {
      setWorkTypesLoading(false);
    }
  };

  const loadWorkSubTypes = async (workTypeId) => {
    try {
      setWorkSubTypesLoading(true);

      const res = await axios.get(
        `${BASE_URL}/api/boq/workSubTypesByWorkType`,
        {
          params: { workTypeId },
          headers: commonHeaders,
        },
      );

      setWorkSubTypes(res.data?.list || []);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to load Work Sub Types",
      );
    } finally {
      setWorkSubTypesLoading(false);
    }
  };

  const handleAddWorkSubTypes = async () => {
    try {
      if (!selectedWorkSubTypeIds?.length) {
        toast.error("Please Select Any Work Sub Type..");
        return;
      }

      const res = await axios.post(
        `${BASE_URL}/api/boq/estimationDetailsByWorkSubTypes`,
        { workInvolvedItemIds: selectedWorkSubTypeIds },
        { headers: commonHeaders },
      );

      const apiRows = res.data?.list || [];

      const newRows = apiRows.map((r) => {
        const workInvolvedItemId =
          r.workInvolvedItemId ??
          r.fld_workinvolveditemid ??
          r.workinvolveditemid;

        const uomId = r.uomId ?? r.fld_uomid ?? 1;
        const uomName = r.uomName ?? r.fld_uomname ?? "";

        const rate = toNum(r.rate ?? r.fld_rate ?? 0);
        const unit = toNum(r.unit ?? r.fld_unit ?? r.fld_perunit ?? 1) || 1;

        const nos = toNum(r.nos ?? r.fld_nos ?? 0);
        const length = toNum(r.length ?? r.fld_length ?? 0);
        const breadth = toNum(r.breadth ?? r.fld_breadth ?? 0);
        const height = toNum(r.height ?? r.fld_height ?? 0);

        const qty = length * breadth * height * nos;
        const amount = unit > 0 ? (qty / unit) * rate : 0;

        return {
          type: "W",
          rowKey: `W-${workInvolvedItemId}`,
          estimationTypeId:
            Number(r.estimationTypeId ?? r.fld_estimationtypeid ?? 0) || 0,
          categoryId: Number(r.categoryId ?? r.fld_categoryid ?? 0) || 0,
          workInvolvedItemId: Number(workInvolvedItemId || 0),
          workTypeName: r.workTypeName ?? r.fld_WorkTypename ?? "",
          workSubTypeName:
            r.workSubTypeName ??
            r.fld_WorkSubTypeName ??
            r.fld_description ??
            "",
          uomId: Number(uomId || 1) || 1,
          uomName,
          nos,
          length,
          breadth,
          height,
          qty,
          rate,
          unit,
          amount,
        };
      });

      setEstimationDetails((prev) => {
        const map = new Map(prev.map((x) => [x.rowKey, x]));
        newRows.forEach((x) => map.set(x.rowKey, x));
        return Array.from(map.values());
      });

      setShowSaveGrid(true);
      setSelectedWorkSubTypeIds([]);
      toast.success("Added to details table");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to add work sub types",
      );
    }
  };

  const convertNumberToWords = (number) => {
    if (!number || number === 0) return "ZERO";
    if (number < 0) return "minus " + convertNumberToWords(Math.abs(number));

    let words = "";

    if (Math.floor(number / 10000000) > 0) {
      words += convertNumberToWords(Math.floor(number / 10000000)) + " CRORE ";
      number %= 10000000;
    }
    if (Math.floor(number / 100000) > 0) {
      words += convertNumberToWords(Math.floor(number / 100000)) + " LAKH ";
      number %= 100000;
    }
    if (Math.floor(number / 1000) > 0) {
      words += convertNumberToWords(Math.floor(number / 1000)) + " THOUSAND ";
      number %= 1000;
    }
    if (Math.floor(number / 100) > 0) {
      words += convertNumberToWords(Math.floor(number / 100)) + " HUNDRED ";
      number %= 100;
    }

    if (number > 0) {
      if (words !== "") words += "AND ";
      const unitsMap = [
        "ZERO",
        "ONE",
        "TWO",
        "THREE",
        "FOUR",
        "FIVE",
        "SIX",
        "SEVEN",
        "EIGHT",
        "NINE",
        "TEN",
        "ELEVEN",
        "TWELVE",
        "THIRTEEN",
        "FOURTEEN",
        "FIFTEEN",
        "SIXTEEN",
        "SEVENTEEN",
        "EIGHTEEN",
        "NINETEEN",
      ];
      const tensMap = [
        "ZERO",
        "TEN",
        "TWENTY",
        "THIRTY",
        "FORTY",
        "FIFTY",
        "SIXTY",
        "SEVENTY",
        "EIGHTY",
        "NINETY",
      ];

      if (number < 20) words += unitsMap[number];
      else {
        words += tensMap[Math.floor(number / 10)];
        if (number % 10 > 0) words += " " + unitsMap[number % 10];
      }
    }

    return words.trim();
  };

  const updateEstRow = (rowKey, patch) => {
    setEstimationDetails((prev) =>
      prev.map((r) => {
        if (r.rowKey !== rowKey) return r;
        const next = { ...r, ...patch };
        return calcRow(next);
      }),
    );
  };

  const totalAmount = estimationDetails.reduce(
    (sum, r) => sum + toNum(r.amount),
    0,
  );

  const saveGridRows = (estimationDetails || []).map((r) => {
    const qty = Number(r.qty || 0);
    const rate = Number(r.rate || 0);
    const perUnit = Number(r.unit || 1) || 1;
    const amount = Number(r.amount || 0);

    return {
      id: r.rowKey,
      workTypeName: r.workTypeName,
      uomName: r.uomName,
      qty,
      rate,
      perUnit,
      amount,
    };
  });

  const toggleSaveRow = (id) => {
    setSelectedDetailIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSaveAll = () => {
    if (selectedDetailIds.length === saveGridRows.length) {
      setSelectedDetailIds([]);
    } else {
      setSelectedDetailIds(saveGridRows.map((x) => x.id));
    }
  };

  const handleAddMaterialsToDetails = () => {
    try {
      if (!selectedItemIds?.length) {
        toast.error("Please select items");
        return;
      }

      const picked = items.filter((x) => selectedItemIds.includes(x.itemId));

      const newRows = picked.map((x) => {
        const rate = 0;
        const unit = 1;
        const nos = 0;
        const length = 0;
        const breadth = 0;
        const height = 0;
        const qty = length * breadth * height * nos;
        const amount = unit > 0 ? (qty / unit) * rate : 0;

        return {
          type: "M",
          rowKey: `M-${x.itemId}`,
          itemId: Number(x.itemId || 0),
          categoryId: Number(x.categoryId || 0),
          estimationTypeId: 0,
          workInvolvedItemId: 0,
          workTypeName: x.categoryName || "MATERIAL",
          workSubTypeName: x.itemName,
          uomId: Number(x.uomId || 1),
          uomName: x.uomName || "Nos",
          nos,
          length,
          breadth,
          height,
          qty,
          rate,
          unit,
          amount,
        };
      });

      setEstimationDetails((prev) => {
        const map = new Map(prev.map((r) => [r.rowKey, r]));
        newRows.forEach((r) => map.set(r.rowKey, r));
        return Array.from(map.values());
      });

      setShowSaveGrid(true);
      setSelectedItemIds([]);
      toast.success("Added to details table");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add materials to details");
    }
  };

  const isEditable =
    pageMode === PAGE_MODE.ADD ||
    (pageMode === PAGE_MODE.PRE && canEdit && tenderStatus !== 1);

  const getUserId = () =>
    Number(
      getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || 0,
    ) || 0;

  const handleSaveClick = () => {
    if (selectedDetailIds.length === 0) {
      setSelectedDetailIds(estimationDetails.map((r) => r.rowKey));
    }
    setTimeout(() => handleSave(), 0);
  };

  const buildPreEstimationPayload = () => {
    const rowsToSave = estimationDetails.filter((r) =>
      selectedDetailIds.includes(r.rowKey),
    );

    if (!rowsToSave.length) {
      toast.error("Please select at least one row to save.");
      return null;
    }

    const loggedUserId = getUserId();
    if (!loggedUserId) {
      toast.error("UserId missing");
      return null;
    }

    return {
      preEstimationId: pageMode === PAGE_MODE.PRE ? preEstimationId : undefined,
      estimationCode: boqCode,
      projectName: workName,
      customerName: tenderAuthority,
      customerAddress: tenderPackageNo,
      remarks: builderName,
      userId: loggedUserId,
      details: rowsToSave.map((r) => {
        const type = (r.type || "W").toUpperCase();

        if (type === "M") {
          return {
            estimationTypeId: 0,
            workTypeId: Number(r.categoryId || 0),
            workSubTypeId: Number(r.itemId || 0),
            nos: toDec(r.nos),
            uomId: Number(r.uomId || 0),
            length: toDec(r.length),
            breadth: toDec(r.breadth),
            height: toDec(r.height),
            qty: toDec(r.qty),
            rate: toDec(r.rate),
            perUnit: toDec(r.unit || 1),
            amount: toDec(r.amount),
            type: "M",
          };
        }

        return {
          estimationTypeId: Number(r.estimationTypeId || 0),
          workTypeId: Number(r.categoryId || 0),
          workSubTypeId: Number(r.workInvolvedItemId || 0),
          nos: toDec(r.nos),
          uomId: Number(r.uomId || 0),
          length: toDec(r.length),
          breadth: toDec(r.breadth),
          height: toDec(r.height),
          qty: toDec(r.qty),
          rate: toDec(r.rate),
          perUnit: toDec(r.unit || 1),
          amount: toDec(r.amount),
          type: "W",
        };
      }),
    };
  };

  const buildFinalEstimationPayload = (preEstimationIdLocal) => {
    const rowsToSave = estimationDetails.filter((r) =>
      selectedDetailIds.includes(r.rowKey),
    );

    if (!rowsToSave.length) {
      toast.error("Please select at least one row to save.");
      return null;
    }

    const loggedUserId = getUserId();
    if (!loggedUserId) {
      toast.error("UserId missing");
      return null;
    }

    return {
      header: {
        preEstimationId: Number(preEstimationIdLocal),
        estimationCode: boqCode,
        projectName: workName,
        customerName: tenderAuthority,
        customerAddress: tenderPackageNo,
        remarks: builderName,
        userId: loggedUserId,
      },
      details: rowsToSave.map((r) => {
        const type = (r.type || "W").toUpperCase();
        const estimationTypeId = Number(r.estimationTypeId || 0);

        return {
          type,
          estimationTypeId: type === "M" ? 0 : estimationTypeId,
          workTypeId: Number(r.categoryId || 0),
          workTypeName: r.workTypeName || (type === "M" ? "MATERIAL" : ""),
          uomName: r.uomName || "",
          qty: toDec(r.qty),
          rate: toDec(r.rate),
          unit: toDec(r.unit || 1),
          amount: toDec(r.amount),
        };
      }),
    };
  };

  const isAddMode = pageMode === PAGE_MODE.ADD;
  const isUpdateMode =
    pageMode === PAGE_MODE.PRE && canEdit && tenderStatus !== 1;

  const handleSave = async () => {
    try {
      const API_BASE_URL = `${BASE_URL}`;

      const prePayload = buildPreEstimationPayload();
      if (!prePayload) return;

      let preEstimationIdLocal = preEstimationId || null;
      const wasPreMode = pageMode === PAGE_MODE.PRE;

      if (pageMode === PAGE_MODE.PRE && preEstimationIdLocal) {
        const res = await axios.put(
          `${API_BASE_URL}/api/estimationDetails/updatePreEstimation`,
          {
            ...prePayload,
            preEstimationId: preEstimationIdLocal,
          },
          {
            headers: commonHeaders,
          },
        );

        if (!res.data?.success) {
          toast.error(res.data?.message || "PreEstimation update failed");
          return;
        }

        preEstimationIdLocal =
          res.data?.preEstimationId || preEstimationIdLocal;
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/api/estimationDetails/savePreEstimation`,
          prePayload,
          {
            headers: commonHeaders,
          },
        );

        if (!res.data?.success) {
          toast.error(res.data?.message || "PreEstimation save failed");
          return;
        }

        preEstimationIdLocal = res.data?.preEstimationId;
        setPreEstimationId(preEstimationIdLocal);
        setPageMode(PAGE_MODE.PRE);
      }

      toast.success(`PreEstimation saved ✅ (ID: ${preEstimationIdLocal})`);

      const finalPayload = buildFinalEstimationPayload(preEstimationIdLocal);
      if (!finalPayload) return;

      if (wasPreMode) {
        const res = await axios.put(
          `${API_BASE_URL}/api/estimationDetails/updateEstimation`,
          finalPayload,
          {
            headers: commonHeaders,
          },
        );

        if (!res.data?.success) {
          toast.error(res.data?.message || "Final Estimation update failed");
          return;
        }

        toast.success("Final Estimation updated ✅");
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/api/estimationDetails/saveEstimation`,
          finalPayload,
          {
            headers: commonHeaders,
          },
        );

        if (!res.data?.success) {
          toast.error(res.data?.message || "Final Estimation save failed");
          return;
        }

        toast.success("Final Estimation saved ✅");
      }

      setSelectedDetailIds([]);
      setCanEdit(false);
      setShowSaveGrid(false);
    } catch (err) {
      console.error("🔥 handleSave error:", err);
      toast.error(err?.response?.data?.message || "Save / Update failed");
    }
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BOQ DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <button
          onClick={() => navigate("/OrderManagement/EstimationDetailsListing")}
        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <FaArrowLeft />
          Back
        </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                BOQ Details
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className={`p-4 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
              <h3 className="text-lg font-semibold">BOQ Information</h3>
            </div>

            {showForm && (
              <div
                className={`transition-all duration-300 ease-in-out ${
                  showForm
                    ? "max-h-none opacity-100"
                    : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div
                  className={`p-6 border-t ${
                    isDark
                      ? "bg-gray-800 border-gray-700"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        BOQ Code
                      </label>
                      <input
                        value={boqCode}
                        disabled
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                        placeholder="EST/1"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Name of the Work  <span className="ml-1 text-red-500">*</span>
                      </label>
                      <input
                        value={workName}
                        disabled={!isEditable}
                        onChange={(e) => setWorkName(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Tender Inviting Authority<span className="ml-1 text-red-500">*</span>
                      </label>
                      <input
                        value={tenderAuthority}
                        disabled={!isEditable}
                        onChange={(e) => setTenderAuthority(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Tender Package No  <span className="ml-1 text-red-500">*</span>
                      </label>
                      <input
                        value={tenderPackageNo}
                        disabled={!isEditable}
                        onChange={(e) => setTenderPackageNo(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Builder Name  <span className="ml-1 text-red-500">*</span>
                      </label>
                      <input
                        value={builderName}
                        disabled={!isEditable}
                        onChange={(e) => setBuilderName(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>

                    {pageMode === "PRE" && tenderStatus !== 1 && !canEdit && (
                      <div className="flex justify-end mb-4">
                        <button
                          type="button"
                          onClick={() => setCanEdit(true)}
                          className={`px-6 py-2 rounded-lg font-semibold shadow ${buttonClass} ${hoverClass} text-white`}
                        >
                          Edit / Add More
                        </button>
                      </div>
                    )}

                    {pageMode === "FINAL" && (
                      <div className="mb-4 text-sm font-semibold text-red-500">
                        This BOQ is finalized. Editing is disabled.
                      </div>
                    )}

                    <div className="col-span-full">
                      <span
                        className={`font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Work Type:
                      </span>

                      <label className="ml-4">
                        <input
                          type="radio"
                          value="work"
                          checked={workType === "work"}
                          onChange={(e) => setWorkType(e.target.value)}
                          className={`mr-1 ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                        />{" "}
                        <span
                          className={isDark ? "text-gray-300" : "text-gray-700"}
                        >
                          Work Type
                        </span>
                      </label>

                      <label className="ml-6">
                        <input
                          type="radio"
                          value="materials"
                          checked={workType === "materials"}
                          onChange={(e) => setWorkType(e.target.value)}
                          className={`mr-1 ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                        />{" "}
                        <span
                          className={isDark ? "text-gray-300" : "text-gray-700"}
                        >
                          Other Materials
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {(pageMode === "ADD" ||
            (pageMode === "PRE" && canEdit && tenderStatus !== 1)) && (
            <>
              {workType === "work" && showEstimationTable && (
                <div
                  className={`rounded-xl border mb-6 overflow-hidden ${
                    isDark ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                            S.No
                          </th>
                          <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                            Estimation Type Code
                          </th>
                          <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                            Estimation Type Name
                          </th>
                          <th className="px-4 py-3 text-center font-semibold">
                            <input
                              type="checkbox"
                              checked={
                                estimationTypes.length > 0 &&
                                selectedRows.length === estimationTypes.length
                              }
                              readOnly
                              className={`rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-blue-600"
                                  : "bg-white border-gray-300 text-blue-600"
                              }`}
                            />{" "}
                            Select All
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {loadingTypes && (
                          <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                            <td
                              colSpan={4}
                              className={`text-center py-6 text-sm ${
                                isDark ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Loading...
                            </td>
                          </tr>
                        )}

                        {!loadingTypes &&
                          estimationTypes.map((item, idx) => (
                            <tr
                              key={item.estimationTypeId}
                              className={`transition-colors duration-200 ${
                                idx % 2 === 0
                                  ? isDark
                                    ? "bg-gray-800"
                                    : "bg-gray-50"
                                  : isDark
                                    ? "bg-gray-900"
                                    : "bg-white"
                              }`}
                            >
                              <td
                                className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                  isDark ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                {idx + 1}
                              </td>

                              <td
                                className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                  isDark ? "text-blue-300" : "text-blue-600"
                                }`}
                              >
                                {item.code}
                              </td>

                              <td
                                className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                  isDark ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                {item.name}
                              </td>

                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedEstimationTypeId ===
                                    item.estimationTypeId
                                  }
                                  onChange={() => {
                                    if (
                                      selectedEstimationTypeId ===
                                      item.estimationTypeId
                                    ) {
                                      setSelectedEstimationTypeId(null);
                                      setShowEstimationTable(true);
                                      setShowWorkTypeTable(false);
                                      setWorkTypes([]);
                                      return;
                                    }

                                    setSelectedEstimationTypeId(
                                      item.estimationTypeId,
                                    );
                                    loadWorkTypes(item.estimationTypeId);
                                    setShowEstimationTable(false);
                                    setShowWorkTypeTable(true);
                                  }}
                                />
                              </td>
                            </tr>
                          ))}

                        {!loadingTypes && estimationTypes.length === 0 && (
                          <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                            <td
                              colSpan={4}
                              className={`text-center py-6 text-sm ${
                                isDark ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              No Records Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {workType === "work" && selectedEstimationTypeId && (
                <div
                  className={`rounded-xl border mb-6 overflow-hidden ${
                    isDark ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <div className="overflow-x-auto">
                    <div className="flex justify-end items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setShowEstimationTable((s) => !s)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm font-semibold shadow ${
                          isDark
                            ? "bg-gray-700 hover:bg-gray-600 text-white"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                        }`}
                      >
                        {showEstimationTable ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                        {showEstimationTable
                          ? "Hide Estimation"
                          : "Show Estimation"}
                      </button>
                    </div>

                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                            S.No
                          </th>
                          <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                            Work Type Code
                          </th>
                          <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                            Work Type Name
                          </th>
                          <th className="px-4 py-3 text-center font-semibold">
                            Select
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {workTypesLoading && (
                          <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                            <td
                              colSpan={4}
                              className={`text-center py-6 text-sm ${
                                isDark ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Loading...
                            </td>
                          </tr>
                        )}

                        {!workTypesLoading &&
                          workTypes.map((w, idx) => (
                            <tr
                              key={w.categoryId}
                              className={`transition-colors duration-200 ${
                                idx % 2 === 0
                                  ? isDark
                                    ? "bg-gray-800"
                                    : "bg-gray-50"
                                  : isDark
                                    ? "bg-gray-900"
                                    : "bg-white"
                              }`}
                            >
                              <td
                                className={`px-4 py-3 text-center ${
                                  isDark ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                {idx + 1}
                              </td>

                              <td
                                className={`px-4 py-3 text-center ${
                                  isDark ? "text-blue-300" : "text-blue-600"
                                }`}
                              >
                                {w.code}
                              </td>

                              <td
                                className={`px-4 py-3 text-center ${
                                  isDark ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                {w.name}
                              </td>

                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedWorkTypeId === w.categoryId}
                                  onChange={() => {
                                    if (selectedWorkTypeId === w.categoryId) {
                                      setSelectedWorkTypeId(null);
                                      setWorkSubTypes([]);
                                      setShowWorkSubTypeTable(false);
                                      return;
                                    }
                                    setSelectedWorkTypeId(w.categoryId);
                                    loadWorkSubTypes(w.categoryId);
                                    setShowWorkSubTypeTable(true);
                                  }}
                                />
                              </td>
                            </tr>
                          ))}

                        {!workTypesLoading && workTypes.length === 0 && (
                          <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                            <td
                              colSpan={4}
                              className={`text-center py-6 text-sm ${
                                isDark ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              No Records Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {workType === "work" && showWorkSubTypeTable && (
                    <div
                      className={`rounded-xl border mb-6 overflow-hidden ${
                        isDark ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className={`${buttonClass} text-white`}>
                            <tr>
                              <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                                S.No
                              </th>
                              <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                                Work Sub Type Code
                              </th>
                              <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                                Work Sub Type Name
                              </th>
                              <th className="px-4 py-3 text-center font-semibold">
                                Select
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {workSubTypesLoading ? (
                              <tr
                                className={
                                  isDark ? "bg-gray-800" : "bg-gray-50"
                                }
                              >
                                <td
                                  colSpan={4}
                                  className={`text-center py-6 text-sm ${
                                    isDark ? "text-gray-400" : "text-gray-500"
                                  }`}
                                >
                                  Loading...
                                </td>
                              </tr>
                            ) : workSubTypes.length ? (
                              workSubTypes.map((st, idx) => (
                                <tr
                                  key={st.workInvolvedItemId ?? idx}
                                  className={`transition-colors duration-200 ${
                                    idx % 2 === 0
                                      ? isDark
                                        ? "bg-gray-800"
                                        : "bg-gray-50"
                                      : isDark
                                        ? "bg-gray-900"
                                        : "bg-white"
                                  }`}
                                >
                                  <td
                                    className={`px-4 py-3 text-center ${
                                      isDark ? "text-gray-300" : "text-gray-900"
                                    }`}
                                  >
                                    {idx + 1}
                                  </td>
                                  <td
                                    className={`px-4 py-3 text-center ${
                                      isDark ? "text-blue-300" : "text-blue-600"
                                    }`}
                                  >
                                    {st.code ?? st.itemCode}
                                  </td>
                                  <td
                                    className={`px-4 py-3 text-center ${
                                      isDark ? "text-gray-300" : "text-gray-900"
                                    }`}
                                  >
                                    {st.description ?? st.workSubTypeName}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedWorkSubTypeIds.includes(
                                        st.workInvolvedItemId,
                                      )}
                                      onChange={() => {
                                        setSelectedWorkSubTypeIds((prev) =>
                                          prev.includes(st.workInvolvedItemId)
                                            ? prev.filter(
                                                (x) =>
                                                  x !== st.workInvolvedItemId,
                                              )
                                            : [...prev, st.workInvolvedItemId],
                                        );
                                      }}
                                    />
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr
                                className={
                                  isDark ? "bg-gray-800" : "bg-gray-50"
                                }
                              >
                                <td
                                  colSpan={4}
                                  className={`text-center py-6 text-sm ${
                                    isDark ? "text-gray-400" : "text-gray-500"
                                  }`}
                                >
                                  No Records Found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end p-4">
                        <button
                          type="button"
                          onClick={handleAddWorkSubTypes}
                          className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {workType === "materials" && (
                <>
                  <div
                    className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
                      isDark ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <div
                      className={`p-6 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Item Category<span className="text-red-500">*</span>
                          </label>
                          <select
                            value={itemCategoryId}
                            onChange={(e) => setItemCategoryId(e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          >
                            <option value="">Select</option>
                            {itemCategories.map((c, i) => (
                              <option
                                key={c.categoryId ?? i}
                                value={c.categoryId}
                              >
                                {c.categoryName}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Item Name<span className="text-red-500">*</span>
                          </label>
                          <input
                            value={itemName}
                            onChange={(e) => setItemName(e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          />
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Item Code<span className="text-red-500">*</span>
                          </label>
                          <input
                            value={itemCode}
                            onChange={(e) => setItemCode(e.target.value)}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          />
                        </div>
                      </div>

                      <div className="flex justify-center mt-6">
                        <button
                          onClick={handleMaterialSearch}
                          className={`px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                        >
                          Search
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`rounded-xl border mb-6 overflow-hidden ${
                      isDark ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className={`${buttonClass} text-white`}>
                          <tr>
                            <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                              S.No
                            </th>
                            <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                              Item Code
                            </th>
                            <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                              Item Name
                            </th>
                            <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                              Part No
                            </th>
                            <th className="px-4 py-3 text-center font-semibold">
                              Select
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {itemsLoading && (
                            <tr
                              className={isDark ? "bg-gray-800" : "bg-gray-50"}
                            >
                              <td
                                colSpan={5}
                                className={`text-center py-6 text-sm ${
                                  isDark ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                Loading...
                              </td>
                            </tr>
                          )}

                          {!itemsLoading &&
                            items.map((it, idx) => (
                              <tr
                                key={it.itemId}
                                className={`transition-colors duration-200 ${
                                  idx % 2 === 0
                                    ? isDark
                                      ? "bg-gray-800"
                                      : "bg-gray-50"
                                    : isDark
                                      ? "bg-gray-900"
                                      : "bg-white"
                                }`}
                              >
                                <td
                                  className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                    isDark ? "text-gray-300" : "text-gray-900"
                                  }`}
                                >
                                  {idx + 1}
                                </td>

                                <td
                                  className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                    isDark ? "text-blue-300" : "text-blue-600"
                                  }`}
                                >
                                  {it.itemCode}
                                </td>

                                <td
                                  className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                    isDark ? "text-gray-300" : "text-gray-900"
                                  }`}
                                >
                                  {it.itemName}
                                </td>

                                <td
                                  className={`px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                    isDark ? "text-gray-300" : "text-gray-900"
                                  }`}
                                >
                                  {it.partNo || ""}
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedItemIds.includes(
                                      it.itemId,
                                    )}
                                    onChange={() => {
                                      setSelectedItemIds((prev) =>
                                        prev.includes(it.itemId)
                                          ? prev.filter((x) => x !== it.itemId)
                                          : [...prev, it.itemId],
                                      );
                                    }}
                                    className={`rounded focus:ring-2 focus:ring-blue-500 ${
                                      isDark
                                        ? "bg-gray-700 border-gray-600 text-blue-600"
                                        : "bg-white border-gray-300 text-blue-600"
                                    }`}
                                  />
                                </td>
                              </tr>
                            ))}

                          {!itemsLoading && items.length === 0 && (
                            <tr
                              className={isDark ? "bg-gray-800" : "bg-gray-50"}
                            >
                              <td
                                colSpan={5}
                                className={`text-center py-6 text-sm ${
                                  isDark ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                No Records Found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {itemsMode === "top100" && (
                      <div
                        className={`p-4 flex justify-center ${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <button
                          onClick={handleViewAllItems}
                          className={`px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                        >
                          View All
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="p-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddMaterialsToDetails}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${buttonClass} ${hoverClass} text-white`}
                    >
                      Add
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          <div
            className={`rounded-xl border mb-6 overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Work Type Name
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Work Sub Type Name
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      NOS
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      UOM
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Length
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Breadth
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Height
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Quantity
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Rate
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Per Unit
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      Amount
                    </th>
                    <th className="px-3 py-3 text-center font-semibold">
                      Select
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {estimationDetails.length === 0 ? (
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                      <td
                        colSpan={13}
                        className={`text-center py-6 text-sm ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    estimationDetails.map((r, idx) => {
                      const rowKey = r.rowKey;
                      const isSelected = selectedDetailIds.includes(rowKey);

                      return (
                        <tr
                          key={rowKey}
                          className={`transition-colors duration-200 ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                                ? "bg-gray-900"
                                : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {idx + 1}
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.workTypeName}
                          </td>

                          <td className="px-3 py-3">
                            <div
                              className={`text-sm leading-relaxed ${
                                isDark ? "text-gray-300" : "text-gray-800"
                              }`}
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 5,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                              title={r.workSubTypeName}
                            >
                              {r.workSubTypeName}
                            </div>
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              value={r.nos ?? 0}
                              disabled={!isEditable}
                              onChange={(e) =>
                                updateEstRow(rowKey, { nos: e.target.value })
                              }
                              className={`w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r.uomName}
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              value={r.length ?? 0}
                              disabled={!isEditable}
                              onChange={(e) =>
                                updateEstRow(rowKey, { length: e.target.value })
                              }
                              className={`w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              value={r.breadth ?? 0}
                              disabled={!isEditable}
                              onChange={(e) =>
                                updateEstRow(rowKey, {
                                  breadth: e.target.value,
                                })
                              }
                              className={`w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              value={r.height ?? 0}
                              disabled={!isEditable}
                              onChange={(e) =>
                                updateEstRow(rowKey, { height: e.target.value })
                              }
                              className={`w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm font-semibold ${
                              isDark ? "text-gray-200" : "text-gray-900"
                            }`}
                          >
                            {toNum(r.qty).toFixed(2)}
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              value={r.rate ?? 0}
                              disabled={!isEditable}
                              onChange={(e) =>
                                updateEstRow(rowKey, { rate: e.target.value })
                              }
                              className={`w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              value={r.unit ?? 1}
                              disabled={!isEditable}
                              onChange={(e) =>
                                updateEstRow(rowKey, { unit: e.target.value })
                              }
                              className={`w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm font-semibold ${
                              isDark ? "text-gray-200" : "text-gray-900"
                            }`}
                          >
                            {toNum(r.amount).toFixed(2)}
                          </td>

                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                setSelectedDetailIds((prev) =>
                                  prev.includes(rowKey)
                                    ? prev.filter((x) => x !== rowKey)
                                    : [...prev, rowKey],
                                )
                              }
                              className={`rounded focus:ring-2 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-blue-600"
                                  : "bg-white border-gray-300 text-blue-600"
                              }`}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div
              className={`p-4 flex justify-between items-center ${
                isDark ? "bg-gray-800" : "bg-gray-50"
              }`}
            >
              <div
                className={`font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}
              >
                Total:{" "}
                {estimationDetails
                  .reduce((sum, r) => sum + toNum(r.amount), 0)
                  .toFixed(2)}
              </div>

              <button
                type="button"
                onClick={() => setShowSaveGrid(true)}
                className={`px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all text-white ${buttonClass}`}
              >
                Add
              </button>
            </div>
          </div>

          {showSaveGrid && saveGridRows.length > 0 && (
            <div
              className={`rounded-xl border mb-6 overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Work Type Name
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        UOM
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Rate
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Per Unit
                      </th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        <input
                          type="checkbox"
                          checked={
                            saveGridRows.length > 0 &&
                            selectedDetailIds.length === saveGridRows.length
                          }
                          onChange={toggleSaveAll}
                        />{" "}
                        Select All
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {saveGridRows.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`transition-colors duration-200 ${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">{idx + 1}</td>
                        <td className="px-4 py-3 text-center">
                          {r.workTypeName}
                        </td>
                        <td className="px-4 py-3 text-center">{r.uomName}</td>
                        <td className="px-4 py-3 text-center">
                          {r.qty.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {r.rate.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {r.perUnit.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {r.amount.toFixed(2)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedDetailIds.includes(r.id)}
                            onChange={() => toggleSaveRow(r.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end p-4">
                <button
                  type="button"
                  disabled={selectedDetailIds.length === 0}
                  onClick={handleSave}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg transition-all ${
                    selectedDetailIds.length === 0
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSave />
                  Save
                </button>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default EstimationDetails;
